import * as PropTypes from 'prop-types';
import type { ReactNode } from 'react';

/**
 * Runtime-белый список сквозных пропсов кнопки: Button пропускает `rest` через
 * `pick(rest, Object.keys(buttonPropTypes))` — это фильтр «мусорных» пропсов
 * .jsx-потребителей (замена на `...rest` меняет поведение, см. CORE_TSX_PLAN.md §2.1).
 */
export const buttonPropTypes = {
  /**
   * HTML тип кнопки: 'button', 'reset', 'submit'
   */
  type: PropTypes.oneOf(['button', 'reset', 'submit']),
  name: PropTypes.string,
  /**
   * FontAwesome имя класса иконки
   * К примеру значение cog выставит класс fa-cog
   */
  icon: PropTypes.string,
  addIconclassName: PropTypes.string,
  text: PropTypes.string,
  /**
   * Нативный HTML title — всплывающая подсказка (нужен кнопкам из одной иконки, без text)
   */
  title: PropTypes.string,
  /**
   * Bootstrap вид кнопки
   * К примеру значение primary выставит класс btn-primary
   * @see https://getbootstrap.com/docs/4.4/components/buttons/
   */
  variant: PropTypes.oneOf([
    'default',
    'primary', 'outline-primary',
    'secondary', 'outline-secondary',
    'info', 'outline-info',
    'success', 'outline-success',
    'warning', 'outline-warning',
    'danger', 'outline-danger',
    'dark', 'outline-dark',
    'light', 'outline-light',
    'link',
  ]),
  href: PropTypes.string,
  /**
   * Bootstrap размер кнопки: 'sm' | 'lg' (класс btn-sm / btn-lg)
   */
  size: PropTypes.oneOf(['sm', 'lg']),
  className: PropTypes.string,
  target: PropTypes.string,
  active: PropTypes.bool,
  block: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  children: PropTypes.any,
  onClick: PropTypes.func,
};

/**
 * Пропсы базовой кнопки (Button). Ключи синхронизированы с {@link buttonPropTypes}
 * (runtime-белый список pick-фильтра) — при изменении обновлять оба места.
 *
 * `variant='default'` — легаси-API: react-bootstrap его не знает (класс не навешивается).
 * `addIconclassName` — историческая опечатка имени, часть публичного API.
 */
export interface ButtonProps {
  /** HTML тип кнопки. @default 'button' */
  type?: 'button' | 'reset' | 'submit';
  name?: string;
  /** FontAwesome имя иконки (значение cog → класс fa-cog). */
  icon?: string;
  /** Дополнительный класс иконки (историческое имя с маленькой буквы). */
  addIconclassName?: string;
  /** Текст кнопки; при пустом тексте иконка без отступа. */
  text?: string;
  /** Нативный HTML title (нужен кнопкам из одной иконки, без text). */
  title?: string;
  /** Bootstrap-вид кнопки. `default` — легаси-значение без класса. */
  variant?: 'default' | 'primary' | 'outline-primary' | 'secondary' | 'outline-secondary' | 'info' | 'outline-info' | 'success' | 'outline-success' | 'warning' | 'outline-warning' | 'danger' | 'outline-danger' | 'dark' | 'outline-dark' | 'light' | 'outline-light' | 'link';
  /** Кнопка-ссылка: рендер `<a>`. */
  href?: string;
  /** Bootstrap-размер (класс btn-sm / btn-lg). */
  size?: 'sm' | 'lg';
  className?: string;
  /** Target для варианта с href. */
  target?: string;
  active?: boolean;
  block?: boolean;
  disabled?: boolean;
  /** Показывать спиннер вместо иконки + disabled. */
  loading?: boolean;
  children?: ReactNode;
  onClick?: (...args: unknown[]) => unknown;
}
